import { useEffect, useRef, useEffectEvent } from "react";
import type { FC, ReactNode } from "react";
import { z } from "zod";
import {
  AuthProvider as ReactOidcContextAuthProvider,
  hasAuthParams,
  useAuth,
  type AuthContextProps,
} from "react-oidc-context";
import { UserManager } from "oidc-client-ts";
import { logInfo } from "../api/logInfo";
import { PageSpinner } from "../shared/Page";
import { authConfig, redirectStorageKey } from "./authConfig";
import { PageErrorBoundary } from "../root/error/ErrorPage";
import { LayoutWhenLoading } from "../root/layout/Layout";
import { useNavigate } from "react-router";
import { logError } from "../api/logError";

type AuthProviderProps = { children: ReactNode };

const userManager = new UserManager(authConfig);
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();

  const onSigninCallback = () => {
    const { pathname, search } = getRedirectUri({ pathname: "/", search: "" });
    navigate(`${pathname}${search}`, { replace: true });
  };
  return (
    <PageErrorBoundary authConfig={{ isAuthenticated: false }} includePlaceholderSidebar>
      <ReactOidcContextAuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
        <AuthProviderInner>{children}</AuthProviderInner>
      </ReactOidcContextAuthProvider>
    </PageErrorBoundary>
  );
};

const LOGIN_HINT_PARAM = "login_hint";
const AuthProviderInner: FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();
  const hasTriedSignInRef = useRef(false);

  const handleSignin = useEffectEvent((auth: AuthContextProps) => {
    const shouldSignin =
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignInRef.current;

    if (!shouldSignin) return;

    const searchParams = new URLSearchParams(location.search);
    const loginHint = searchParams.get(LOGIN_HINT_PARAM) ?? undefined;
    if (loginHint) searchParams.delete(LOGIN_HINT_PARAM);

    const search = searchParams.toString() ? `?${searchParams.toString()}` : "";
    const redirectState: RedirectUri = { pathname: location.pathname, search };
    setRedirectUri(redirectState);
    hasTriedSignInRef.current = true;
    return auth.signinRedirect({ state: redirectState, [LOGIN_HINT_PARAM]: loginHint ?? undefined });
  });

  /** Sign in */
  useEffect(() => void handleSignin(auth), [auth]);

  /** Handle silent renew errors */
  useEffect(() => {
    const handleSilentRenewError = async (error: Error) => {
      logError(new Error(`Visma Connect silent renew error: ${JSON.stringify(error)}`), {
        loggedFrom: "AuthProvider(handleSilentRenewError)",
      });
      await auth.removeUser();
      await handleSignin(auth);
    };
    userManager.events.addSilentRenewError(handleSilentRenewError);
    return () => userManager.events.removeSilentRenewError(handleSilentRenewError);
  }, [auth]);

  /** Log auth info */
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      logInfo(`Visma Connect user login succeeded. ${auth.user.profile.sub} has signed in successfully.`);
    }
    if (auth.activeNavigator === "signoutRedirect" && auth.isAuthenticated && auth.user) {
      logInfo(`Visma Connect user logout succeeded. ${auth.user.profile.sub} has logged out successfully.`);
    }
    if (auth.error)
      logError(new Error(`Visma Connect auth error: ${JSON.stringify(auth.error)}`), {
        loggedFrom: "AuthProvider(useEffect)",
      });
  }, [auth]);

  if (auth.isLoading)
    return (
      <LayoutWhenLoading>
        <PageSpinner />
      </LayoutWhenLoading>
    );
  return <>{children}</>;
};

type RedirectUri = { pathname: string; search: string };

const setRedirectUri = (uri: RedirectUri) => sessionStorage.setItem(redirectStorageKey, JSON.stringify(uri));

const getRedirectUri = (fallback: RedirectUri): RedirectUri => {
  const item = sessionStorage.getItem(redirectStorageKey);
  if (!item) return fallback;

  try {
    const uri: unknown = JSON.parse(item);

    if (typeof uri !== "object" || !uri) return fallback;

    return {
      pathname: "pathname" in uri && typeof uri.pathname === "string" ? uri.pathname : fallback.pathname,
      search: "search" in uri && typeof uri.search === "string" ? uri.search : fallback.search,
    };
  } catch {
    return fallback;
  }
};

const AuthUserSchema = z.object({
  email: z.string().catch("Unknown"),
  createdAt: z.number().catch(0),
  picture: z.string().optional(),
});

type AuthUser = z.infer<typeof AuthUserSchema>;

export const useAuthUser = (): AuthUser => {
  const auth = useAuth();

  if (!auth.user) throw { errorCode: AUTH_ERROR_CODES.NO_USER };

  const profile = auth.user.profile;

  return AuthUserSchema.parse({ email: profile.email, createdAt: profile.created_at, picture: profile.picture });
};

export const AUTH_ERROR_CODES = {
  NO_TOKEN: "NO_TOKEN",
  NO_USER: "NO_USER",
  NOT_AUTHORIZED: "NOT_AUTHORIZED",
} as const;

export const AuthErrorSchema = z.object({
  errorCode: z.enum(Object.values(AUTH_ERROR_CODES)),
});

export const isAuthError = (error: unknown): error is z.infer<typeof AuthErrorSchema> =>
  AuthErrorSchema.safeParse(error).success;

export const useGetHandleAuthError = () => {
  const auth = useAuth();
  return (error: unknown) => {
    if (!isAuthError(error)) return;

    const { errorCode } = error;
    const shouldSignin = errorCode === AUTH_ERROR_CODES.NO_TOKEN || errorCode === AUTH_ERROR_CODES.NO_USER;
    const shouldSignout = errorCode === AUTH_ERROR_CODES.NOT_AUTHORIZED;

    if (shouldSignin && auth.activeNavigator !== "signoutRedirect") auth.signinRedirect();
    if (shouldSignout) auth.signoutRedirect();
  };
};
